import type { PromotionCouponWithPromotion } from "@/modules/promotions/domain/types";
import { formatDateLocal } from "@/shared/lib/date-time";
import { RowActions, type RowAction } from "@/shared/ui/RowActions";
import { confirm } from "@/shared/lib/confirm";
import { DataTable, createSortableColumn, TanstackReactTable } from "@/shared/ui/data-table";
import { Badge } from "@/components/ui/badge";



import { EditIcon, PowerIcon, PowerOffIcon, TrashIcon } from "lucide-react";

type CouponsTableProps = {
  data: PromotionCouponWithPromotion[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  onEdit: (row: PromotionCouponWithPromotion) => void;
  onToggleActive: (id: string, isActive: boolean) => void | Promise<void>;
  onDelete: (id: string) => void | Promise<void>;
};

export function CouponsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onSortingChange,
  onPaginationChange,
  onEdit,
  onToggleActive,
  onDelete,
}: CouponsTableProps) {
  const columns: TanstackReactTable.ColumnDef<PromotionCouponWithPromotion>[] =
    [
      createSortableColumn<PromotionCouponWithPromotion>("code", "ລະຫັດຄູປອງ", {
        size: 150,
      }),
      {
        id: "promotionName",
        header: "ໂປຣໂມຊັນ",
        size: 200,
        cell: ({ row }) => (
          <span className="text-muted-foreground text-sm">
            {row.original.promotionName}
          </span>
        ),
        enableSorting: false,
      },
      {
        id: "limits",
        header: "ຈຳກັດ",
        size: 180,
        cell: ({ row }) => {
          const { maxRedemptions, perCustomerLimit } = row.original;
          const hasMaxRedemptions = maxRedemptions != null;
          const hasPerCustomerLimit = perCustomerLimit != null;

          if (!hasMaxRedemptions && !hasPerCustomerLimit) {
            return <span className="text-muted-foreground text-sm">ບໍ່ຈຳກັດ</span>;
          }

          return (
            <div className="space-y-1">
              {hasMaxRedemptions && hasPerCustomerLimit && (
                <div className="text-muted-foreground text-xs">
                  <span className="font-medium">ສູງສຸດ:</span>{" "}
                  {maxRedemptions.toLocaleString("lo-LA")} /{" "}
                  {perCustomerLimit.toLocaleString("lo-LA")} ຕໍ່ລູກຄ້າ
                </div>
              )}
            </div>
          );
        },
        enableSorting: false,
      },
      {
        id: "redemptionCount",
        header: "ຈຳນວນທີ່ໃຊ້ແລ້ວ",
        size: 200,
        cell: ({ row }) => {
          const { maxRedemptions, redemptionCount } = row.original;
          const hasLimit = maxRedemptions != null && maxRedemptions > 0;
          const percentage = hasLimit
            ? Math.min((redemptionCount / maxRedemptions) * 100, 100)
            : 0;

          return (
            <div className="space-y-1">
              {hasLimit ? (
                <>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className={`h-full transition-[width] duration-300 ${
                        percentage >= 100
                          ? "bg-destructive"
                          : percentage >= 80
                            ? "bg-amber-500"
                            : "bg-primary"
                      }`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">
                      {redemptionCount.toLocaleString("lo-LA")} /{" "}
                      {maxRedemptions.toLocaleString("lo-LA")}
                    </span>
                    <span className="text-muted-foreground">
                      {Math.round(percentage)}%
                    </span>
                  </div>
                </>
              ) : (
                <span className="font-medium text-sm">
                  {redemptionCount.toLocaleString("lo-LA")} ຄັ້ງ
                </span>
              )}
            </div>
          );
        },
        enableSorting: false,
      },
      {
        id: "isActive",
        header: "ສະຖານະ",
        size: 100,
        cell: ({ row }) => (
          <Badge variant={row.original.isActive ? "default" : "secondary"}>
            {row.original.isActive ? "ເປີດໃຊ້ງານ" : "ປິດໃຊ້ງານ"}
          </Badge>
        ),
        enableSorting: false,
      },
      createSortableColumn<PromotionCouponWithPromotion>(
        "createdAt",
        "ວັນທີສ້າງ",
        {
          size: 110,
          cell: ({ row }) => formatDateLocal(row.original.createdAt),
        },
      ),
      {
        id: "actions",
        header: "",
        size: 180,
        cell: ({ row }) => {
          const actions: RowAction[] = [];
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original),
          });
          if (row.original.isActive) {
            actions.push({
              label: "ປິດໃຊ້ງານ",
              variant: "secondary",
              icon: <PowerOffIcon className="h-4 w-4" />,
              onClick: async () => {
                const ok = await confirm({
                  title: "ປິດໃຊ້ງານຄູປອງ",
                  description: `ຄູປອງ "${row.original.code}" ຈະບໍ່ສາມາດໃຊ້ໄດ້ອີກ. ຕ້ອງການດຳເນີນການບໍ?`,
                  actionText: "ປິດໃຊ້ງານ",
                  cancelText: "ຍົກເລີກ",
                });
                if (ok) onToggleActive(row.original.id, false);
              },
            });
          } else {
            actions.push({
              label: "ເປີດໃຊ້ງານ",
              icon: <PowerIcon className="h-4 w-4" />,
              onClick: () => onToggleActive(row.original.id, true),
            });
          }
          actions.push({
            label: "ລຶບ",
            variant: "destructive",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ລຶບຄູປອງ",
                description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບຄູປອງ "${row.original.code}"?`,
                actionText: "ລຶບ",
                ActionProps: { variant: "destructive" },
                cancelText: "ຍົກເລີກ",
              });
              if (ok) onDelete(row.original.id);
            },
          });
          return <RowActions actions={actions} maxInline={2} />;
        },
        enableSorting: false,
      },
    ];

  return (
    <DataTable<PromotionCouponWithPromotion, unknown>
      noDataMessage="ບໍ່ພົບຄູປອງ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
    />
  );
}
